import { observer } from 'mobx-react-lite';
import { ChangeEvent, useState } from 'react';
import {
  Button,
  CheckboxField,
  TextField,
  ValidationError,
} from '../../../../../components';
import { useStore } from '../../../../../store';
import { onSuccess } from '../../../../../utils/errors';
import KeyLabelField from './KeyLabelField';

const HINT_TEXT = [
  'Type or paste new keys here.',
  'To enter multiple keys use a new line to separate.',
].join('\n');

const KEY_REGEX = /^[\w-]+$/;

export default observer(function ImportPanel({
  onImport,
  productId,
}: {
  onImport: () => void;
  productId: string;
}) {
  const [label, setLabel] = useState('');
  const [keysText, setKeysText] = useState('');
  const [keys, setKeys] = useState<Key[]>([]);
  const [markDistributed, setMarkDistributed] = useState(false);
  const { pulseKeyStore } = useStore();

  const errors = keys
    .filter((key) => key.error)
    .map(({ key, error }) => `${key}: ${error}`);

  async function onImportClick() {
    await pulseKeyStore.importSerials(
      productId,
      keys.map((k) => k.key),
      label,
      markDistributed,
    );

    onImport();

    const count = keys.length;
    onSuccess(`${count} serial${count === 1 ? '' : 's'} successfully imported`);

    setKeysText('');
  }

  function onType(e: ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    setKeysText(text);
    setKeys(extractKeys(text));
  }

  return (
    <>
      <div>
        <KeyLabelField
          emptyTitle="Without label"
          label="Apply label"
          labelCreating
          onChange={setLabel}
          sx={{ width: 192 }}
          value={label}
        />

        <CheckboxField
          label="Mark distributed"
          onChange={(e) => setMarkDistributed(e.target.checked)}
          sx={{ mt: 3, ml: 4 }}
          value={markDistributed}
        />
      </div>

      <TextField
        errorText={errors.length > 0 && <ValidationError error={errors} />}
        helperText="Please avoid using mixed case serials - all lowercase or uppercase only"
        label="Insert serials here"
        margin="dense"
        multiline
        onChange={onType}
        placeholder={HINT_TEXT}
        rows={5}
        value={keysText}
        variant="outlined"
      />

      <Button
        disabled={keys.length === 0 || errors.length > 0}
        onClick={onImportClick}
        size="small"
        type="primaryText"
      >
        Import serials
      </Button>
    </>
  );
});

function extractKeys(multilineText: string): Key[] {
  return multilineText
    .split('\n')
    .map((keyText) => extractKey(keyText))
    .filter(Boolean) as Key[];
}

function extractKey(keyText: string): Key | undefined {
  const key = keyText.trim();
  if (!key) {
    return undefined;
  }

  if (key.length < 10) {
    return { key, error: 'Minimal key length is 10 characters' };
  }

  if (!KEY_REGEX.test(key)) {
    return {
      key,
      error: 'Allowed characters: numbers, letters, "-", "_"',
    };
  }

  return { key };
}

interface Key {
  error?: string;
  key: string;
}
